
import Enumerable from "linq";
import { Component, Mixins } from "vue-property-decorator";
import {
  Size,
} from "../../../../../base/domain/drawing";
import {
  IPaper,
} from "../../../../../base/domain/system/document";
import {
  IFormatSpecification,
  FormatPageStructureEditor,
  IFormatObjectEditable,
  FormatObjectsEditor,
  IEditableFormatImage,
  IEditableFormatLayer,
} from "../../../../domain/editableFormat";
import { default as FormatStructureEditorStoreReferableMixin } from "./FormatStructureEditorStoreReferableMixin.vue";

@Component
export default class FormatPageStructureEditableMixin extends Mixins(FormatStructureEditorStoreReferableMixin) {
  // ModelEditableMixinと同時に使う事.ModelEditableMixin利用APIの宣言.
  writePropertyWhenValid: (writer: (source: FormatPageStructureEditor) => void) => void;
  readPropertyOrDefault: <T>(reader: (source: FormatPageStructureEditor) => T, defaultValue: T) => T;

  get specification(): IFormatSpecification {
    return this.readPropertyOrDefault(s => s.getSpecification(), null);
  }

  get papers(): IPaper[] {
    return this.specification ? this.specification.papers : [];
  }

  get objects(): IFormatObjectEditable[] {
    return this.readPropertyOrDefault(s => s.getObjects(), []);
  }

  get objectCount(): number {
    return this.objects.length;
  }

  get pageKey(): number {
    return this.readPropertyOrDefault(s => s.pageKey().getValue(), null);
  }

  get size(): Size {
    return this.readPropertyOrDefault(s => Size.from(s.size().getValue()), new Size(0, 0));
  }

  set size(value: Size) {
    this.writePropertyWhenValid(s => s.size().setValue(Size.to(value)));
  }

  get isHorizontal(): boolean {
    return this.readPropertyOrDefault(s => s.isHorizontal().getValue(), false);
  }

  set isHorizontal(value: boolean) {
    this.writePropertyWhenValid(s => s.isHorizontal().setValue(value));
  }

  get image(): IEditableFormatImage {
    return this.readPropertyOrDefault(s => s.image().getValue(), null);
  }

  set image(value: IEditableFormatImage) {
    this.writePropertyWhenValid(s => s.image().setValue(value));
  }

  get layer(): IEditableFormatLayer {
    return this.readPropertyOrDefault(s => s.layer().getValue(), null);
  }

  set layer(value: IEditableFormatLayer) {
    this.writePropertyWhenValid(s => s.layer().setValue(value));
  }

  get shouldPrint(): boolean {
    return this.readPropertyOrDefault(s => s.shouldPrint().getValue(), false);
  }

  set shouldPrint(value: boolean) {
    this.writePropertyWhenValid(s => s.shouldPrint().setValue(value));
  }

  get shouldPrintOnFirstRegistered(): boolean {
    return this.readPropertyOrDefault(s => s.shouldPrintOnFirstRegistered().getValue(), false);
  }

  set shouldPrintOnFirstRegistered(value: boolean) {
    this.writePropertyWhenValid(s => s.shouldPrintOnFirstRegistered().setValue(value));
  }

  get editingOnly(): boolean {
    return this.readPropertyOrDefault(s => s.editingOnly().getValue(), false);
  }

  set editingOnly(value: boolean) {
    this.writePropertyWhenValid(s => s.editingOnly().setValue(value));
  }

  reselectObjects(...objects: IFormatObjectEditable[]): void {
    this.writePropertyWhenValid(s => {
      s.deselectAllObjects();
      s.selectObjects(...objects);
    });
  }

  selectObjects(...objects: IFormatObjectEditable[]): void {
    this.writePropertyWhenValid(s => s.selectObjects(...objects));
  }

  deselectObjects(...objects: IFormatObjectEditable[]): void {
    this.writePropertyWhenValid(s => s.deselectObjects(...objects));
  }

  get selectedObjects(): IFormatObjectEditable[] {
    return this.readPropertyOrDefault(s => s.getSelectedObjects(), []);
  }

  get lastSelectedObject(): IFormatObjectEditable {
    return this.readPropertyOrDefault(s => s.getLastSelectedObject(), null);
  }

  selectAllObjects(): void {
    this.writePropertyWhenValid(s => s.selectAllObjects());
  }

  deselectAllObjects(): void {
    this.writePropertyWhenValid(s => s.deselectAllObjects());
  }

  rangeSelectionObjects(from: IFormatObjectEditable, to: IFormatObjectEditable): IFormatObjectEditable[] {
    return this.readPropertyOrDefault(s => s.rangeSelectionObjects(from, to), []);
  }

  get selectedObjectCount(): number {
    return this.selectedObjects.length;
  }

  get hasSelectedObjects(): boolean {
    return !this.areAllObjectsDeselected;
  }

  get areAllObjectsSelected(): boolean {
    return (this.objectCount <= this.selectedObjectCount);
  }

  get areAllObjectsDeselected(): boolean {
    return (this.selectedObjectCount <= 0);
  }

  get isSingleObjectSelected(): boolean {
    return (this.selectedObjectCount === 1);
  }

  get selectedSingleObject(): IFormatObjectEditable {
    return this.isSingleObjectSelected
      ? this.selectedObjects[0]
      : null;
  }

  set selectedSingleObject(object: IFormatObjectEditable) {
    this.writePropertyWhenValid(s => s.updateObject(object));
  }

  get selectedMultipleObjects(): FormatObjectsEditor {
    return this.hasSelectedObjects
      ? this.readPropertyOrDefault(s => new FormatObjectsEditor(this.selectedObjects), null)
      : null;
  }

  set selectedMultipleObjects(editor: FormatObjectsEditor) {
    this.writePropertyWhenValid(s => {
      for (const o of editor.source()) {
        s.updateObject(o);
      }
    });
  }

  get selectedObjectsEditor(): IFormatObjectEditable | FormatObjectsEditor {
    return this.isSingleObjectSelected
      ? this.selectedSingleObject
      : this.selectedMultipleObjects;
  }

  set selectedObjectsEditor(source: IFormatObjectEditable | FormatObjectsEditor) {
    if (source instanceof FormatObjectsEditor) {
      this.selectedMultipleObjects = source;
    } else {
      this.selectedSingleObject = source;
    }
  }

  removeSelectedObjects(isAdditional: (source: IFormatObjectEditable) => boolean = (s) => false) {
    this.writePropertyWhenValid(s => {
      // indexがずれないよう後ろから削除する.reverse()で破壊しないようにconcat()でshallow copyしてから.
      this.extractSelectedObjects(isAdditional)
        .concat()
        .reverse()
        .forEach(o => s.removeObject(o));
    });
  }

  extractSelectedObjects(isAdditional: (source: IFormatObjectEditable) => boolean = (s) => false) {
    return Enumerable.from(this.objects)
      .where(o => o.isSelected() || isAdditional(o))
      .toArray();
  }

  get isRaisable() {
    return (0 < this.raisableCount);
  }

  get raisableCount(): number {
    return this.hasSelectedObjects
      ? Enumerable.from(this.objects).reverse().select((o, i) => ({ o, i })).first(p => p.o.isSelected()).i
      : 0;
  }

  raiseSelectedObjects(): void {
    this.shiftSelectedObjects(1);
  }

  raiseSelectedObjectsToTop(): void {
    this.shiftSelectedObjects(this.raisableCount);
  }

  shiftSelectedObjects(count: number): void {
    this.writePropertyWhenValid(s => {
      const selectedObjects = s.getSelectedObjects().concat();
      if (0 < count) {
        selectedObjects.reverse();  // 処理順がおかしくならないよう反転.ここで使ってお終いのインスタンスなので破壊的でいい.
      }
      selectedObjects.forEach(o => s.shiftObject(o, count));
    });
  }

  get isLowerable() {
    return (0 < this.lowerableCount);
  }

  get lowerableCount(): number {
    return this.hasSelectedObjects
      ? Enumerable.from(this.objects).select((o, i) => ({ o, i })).first(p => p.o.isSelected()).i
      : 0;
  }

  lowerSelectedObjects(): void {
    this.shiftSelectedObjects(-1);
  }

  lowerSelectedObjectsToBottom(): void {
    this.shiftSelectedObjects(-this.lowerableCount);
  }
}
